// =============================================================================
// useFangateLiveFilter
//
// Shared helpers for derived item state — moderation status, consent status,
// and the rolled-up "is this item live?" predicate used by both the Vault
// Library grid and the chat side-panel.
//
// What "live" means:
//   • Moderation = approved (AI or manual review cleared it)
//   • AND consent is either NOT required OR every tagged co-creator accepted
// A non-live item is anything not meeting both bars (in-review, rejected,
// pending consent, declined, etc.).
//
// Why a composable: keeps a single source of truth for the documented
// veriff_status enum mapping (5 values, see docs.fangate.app) plus the lazy
// consent-fetch cache. Components import the helpers and the cache stays
// per-inbox so two panels for different creators don't pollute each other.
// =============================================================================
import { reactive, ref, computed, watch } from 'vue';
import FangateAPI from '../../../../api/fangate';

// Documented veriff_status enum — fangate.app/api-reference/products.html
const VERIFF_PRIORITY = ['manual_rem', 'ai_removed', 'unchecked', 'manual_appr', 'approved'];

// Case-insensitive mapping of every documented + plausible variant string
// onto the five canonical keys. Order matters: explicit "Manual Approval"
// / "Manual Removal" before generic /approv|removed/ fallbacks.
export const mapVeriffStatus = raw => {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return null;
  if (s.includes('manual removal'))                 return 'manual_rem';
  if (s.includes('manual approval'))                return 'manual_appr';
  if (s.includes('ai removal'))                     return 'ai_removed';
  if (s === 'approved' || s.includes('approv'))     return 'approved';
  if (s === 'unchecked' || s.includes('unchecked')) return 'unchecked';
  if (s.includes('removed') || s.includes('reject')) return 'ai_removed';
  if (s.includes('manual')  || s.includes('review')) return 'unchecked';
  if (s === 'pending' || s.includes('pending'))      return 'unchecked';
  return null;
};

// Worst-case roll-up across the product's top-level fields + every media
// item it contains.
export const productModerationKey = p => {
  if (!p) return null;
  const seen = new Set();
  const top = mapVeriffStatus(
    p.veriff_status || p.moderation_status || p.status || p.removal_status
    || (p.is_removed ? 'AI Removal' : '')
  );
  if (top) seen.add(top);
  const media = Array.isArray(p.media) ? p.media : [];
  for (const m of media) {
    const k = mapVeriffStatus(m.veriff_status);
    if (k) seen.add(k);
  }
  if (!seen.size) return null;
  for (const k of VERIFF_PRIORITY) if (seen.has(k)) return k;
  return null;
};

// Aggregate a consent-creator-tags array into an overall state + counts.
// rescinded/withdrawn/revoked are treated as a negative outcome alongside
// declined/rejected so the roll-up colour stays consistent.
export const aggregateConsent = items => {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return { overall: 'empty', total: 0, counts: { accepted: 0, pending: 0, declined: 0 } };
  const counts = { accepted: 0, pending: 0, declined: 0 };
  for (const t of list) {
    const s = String(t.status || t.decision || 'pending').toLowerCase();
    if (s.startsWith('accept')) counts.accepted++;
    else if (s.startsWith('declin') || s.startsWith('reject')
          || s.startsWith('rescind') || s.startsWith('withdraw') || s.startsWith('revoke')) counts.declined++;
    else counts.pending++;
  }
  let overall = 'accepted';
  if (counts.declined)     overall = 'declined';
  else if (counts.pending) overall = 'pending';
  return { overall, total: list.length, counts };
};

// Factory — each component instance gets its own cache scoped to the inbox
// id. Pass `inboxId` reactively so switching creators resets state.
export function useFangateLiveFilter({ inboxIdRef, liveOnlyStorageKey } = {}) {
  // Cache: { [productId]: { overall, counts, total } }
  const consentCache = reactive({});
  const consentInFlight = new Set();
  const consentQueue = [];
  const CONCURRENCY = 4;

  // liveOnly toggle, persisted per inbox in localStorage so each creator's
  // preference survives reload + inbox switch.
  const storageKey = () =>
    (liveOnlyStorageKey || 'fangate_live_only') + (inboxIdRef?.value ? `_${inboxIdRef.value}` : '');
  const readPersisted = () => {
    try { return localStorage.getItem(storageKey()) === 'true'; } catch (_) { return false; }
  };
  const liveOnly = ref(readPersisted());
  watch(liveOnly, v => {
    try { localStorage.setItem(storageKey(), v ? 'true' : 'false'); } catch (_) {}
  });
  // Reset when inbox changes — different creator, different cache.
  if (inboxIdRef) {
    watch(inboxIdRef, () => {
      Object.keys(consentCache).forEach(k => delete consentCache[k]);
      consentInFlight.clear();
      consentQueue.length = 0;
      liveOnly.value = readPersisted();
    });
  }

  const drainQueue = () => {
    while (consentQueue.length && consentInFlight.size < CONCURRENCY) {
      const id = consentQueue.shift();
      if (consentCache[id] || consentInFlight.has(id)) continue;
      consentInFlight.add(id);
      FangateAPI.listProductConsentTags(id)
        .then(items => { consentCache[id] = aggregateConsent(items); })
        .catch(() => { consentCache[id] = { overall: 'unknown', total: 0, counts: { accepted: 0, pending: 0, declined: 0 } }; })
        .finally(() => {
          consentInFlight.delete(id);
          if (consentQueue.length) drainQueue();
        });
    }
  };

  // Queue only consent-required products that aren't cached/in-flight yet.
  const enqueueConsentFetches = (products) => {
    for (const p of products || []) {
      if (!p?.is_should_consent) continue;
      if (consentCache[p.id] || consentInFlight.has(p.id)) continue;
      if (!consentQueue.includes(p.id)) consentQueue.push(p.id);
    }
    drainQueue();
  };

  // Per-product consent state key (mirrors Vault's productConsentKey).
  const productConsentKey = p => {
    if (!p) return 'none';
    if (!p.is_should_consent) return 'none';
    const cached = consentCache[p.id];
    if (!cached) return 'loading';
    return cached.overall;
  };

  // "Live" = a fan can actually access/buy the product right now. Returns:
  //   true   — definitely live, safe to show in chat
  //   false  — definitely NOT live (rejected, in review, declined, pending)
  //   null   — consent fetch still in flight, can't decide yet
  //
  // Fangate ships its own authoritative purchasability flag, `is_accessible`,
  // which it computes server-side from moderation + consent + age + publish
  // state. When present we trust it directly — it's the single source of
  // truth and avoids the false negatives the reconstruction below produced
  // (e.g. is_should_consent=true with no consent tags rolled up to 'empty'
  // and wrongly hid a live product). The veriff_status + consent reconstruction
  // remains only as a fallback for responses that omit is_accessible.
  const isLive = p => {
    if (typeof p?.is_accessible === 'boolean') return p.is_accessible;
    const mod = productModerationKey(p);
    if (mod !== 'approved' && mod !== 'manual_appr') return false;
    if (!p?.is_should_consent) return true;
    const c = productConsentKey(p);
    if (c === 'accepted') return true;
    if (c === 'loading')  return null;
    return false;
  };

  return {
    liveOnly,
    consentCache,
    enqueueConsentFetches,
    productModerationKey,
    productConsentKey,
    isLive,
  };
}
